import React from 'react';
import { createAITTheme } from '@this/shared/ui/theme';
import type Trip from '@this/domain/trip/trip2';
import type OrderItem from '@this/domain/order_item2';
import type { Element } from '@this/domain/order_item2';
import type TravelerInformation from '@this/src/domain/traveler_information';

// 共通の型定義
export interface ItemPriceProps {
  orderItem: OrderItem;
  changedPrice: number;
  ticketStateType: 'priceChanged' | 'priceNotChanged' | 'noFlight' | 'notApplicable';
}

export type OrderItemDescriptionProps = ItemPriceProps & {
  tripId: number;
  trip: Trip;
};

export interface OrderItemDescriptionWhenRequestFormProps {
  trip: Trip;
}

// 共通ユーティリティ関数
export const formatTravelerText = (el: Element, tst: TravelerInformation, i: number) => {
  let str = '';
  if (el.type === 'transport') {
    if (tst.shinkansenTicketTypeText) {
      str = `券種: ${tst.shinkansenTicketTypeText}, `;
    }
    if (tst.shinkansenSeatTypeText) {
      str += `座席: ${tst.shinkansenSeatTypeText}, `;
    }
    if (tst.shinkansenSeatTypeText) {
      str += `座席希望: ${tst.shinkansenSeatTypeText}`;
    }
  }
  return `出張者:${i + 1}人目　　(${tst.lastNameRoman} ${tst.firstNameRoman})　${str}`;
};

// メディアクエリ判定用のhook
const useMediaQuery = (query: string) => {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);

  return matches;
};

// 共通コンポーネント
interface SectionDescTextProps {
  isOld: boolean;
  className?: string;
  children: React.ReactNode;
}

export const SectionDescText: React.FC<SectionDescTextProps> = ({ isOld, className, children }) => {
  const theme = createAITTheme();
  const style: React.CSSProperties = {
    color: className === 'red' ? theme.tokens.colors.danger.primary : undefined,
    textDecoration: isOld ? 'line-through' : undefined
  };

  return (
    <span className={className} style={style}>
      {children}
    </span>
  );
};

export const OutlineBox: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const theme = createAITTheme();

  const style: React.CSSProperties = {
    border: `1px solid ${theme.tokens.colors.border.divider}`,
    marginTop: '-1px',
    padding: isMobile ? '6px' : '20px',
    margin: '10px 0'
  };

  return <div style={style}>{children}</div>;
};

export const SectionApplication: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const style: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexDirection: isMobile ? 'column' : 'row'
  };

  return <div style={style}>{children}</div>;
};

export const SectionApplicationInfo: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: isMobile ? '100%' : '68%',
    padding: '10px'
  };

  return <div style={style}>{children}</div>;
};

export const SectionApplicationBox: React.FC = ({ children }) => {
  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  };

  return <div style={style}>{children}</div>;
};

export const SectionLabel: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const style: React.CSSProperties = {
    padding: '10px',
    flexBasis: isMobile ? undefined : '16%',
    width: isMobile ? '100%' : undefined,
    fontWeight: 'bold'
  };

  return <div style={style}>{children}</div>;
};

export const SectionMarket: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const style: React.CSSProperties = {
    paddingTop: '10px',
    width: isMobile ? '100%' : '16%',
    textAlign: isMobile ? 'left' : 'right',
    paddingLeft: isMobile ? '10px' : undefined
  };

  return <div style={style}>{children}</div>;
};

export const SectionPrice: React.FC = ({ children }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  const style: React.CSSProperties = {
    padding: '5px',
    fontWeight: 'bold',
    display: 'flex',
    width: isMobile ? '100%' : '90%',
    justifyContent: 'space-between',
    alignItems: 'center'
  };

  return <div style={style}>{children}</div>;
};

export const SectionBudgetHr: React.FC = () => {
  const theme = createAITTheme();
  const style: React.CSSProperties = {
    width: '100%',
    border: `1px solid ${theme.tokens.colors.border.divider}`,
    margin: '5px 0'
  };

  return <hr style={style} />;
};

export const SectionDesc: React.FC = ({ children }) => {
  const style: React.CSSProperties = {
    marginBottom: '10px'
  };

  return <div style={style}>{children}</div>;
};
